import React, { useState, useEffect } from 'react';
import { HashRouter, Routes, Route, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import { supabase } from './supabase';
import ScrollToTop from './components/ScrollToTop';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';
import './App.css';

const { 
  FiPhone, FiMail, FiDollarSign, FiFileText, 
  FiCheck, FiArrowRight, FiShield, 
  FiActivity, FiZap, FiTarget, FiHelpCircle,
  FiUserCheck 
} = FiIcons;

const FORMSPREE_ID = 'xojdlgvd';
const PHONE_NUMBER = '305-630-8128';
const EMAIL_ADDRESS = 'acquisitions@quantassetpartners.com';

function LandingPage() {
  const [formStep, setFormStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);
  const [formData, setFormData] = useState({
    propertyAddress: '', state: '', county: '', apn: '',
    sellerName: '', phone: '', email: '',
    timeline: '', reasoning: '', additionalInfo: ''
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const submitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (import.meta.env.VITE_SUPABASE_URL) {
        await supabase.from('property_leads_20240520').insert([{
          property_address: formData.propertyAddress,
          state: formData.state,
          county: formData.county,
          seller_name: formData.sellerName,
          phone: formData.phone,
          email: formData.email,
          timeline: formData.timeline,
          reasoning: formData.reasoning,
          additional_info: formData.additionalInfo
        }]);
      }
      const response = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          subject: `NEW LAND LEAD: ${formData.sellerName} (${formData.state})`,
          _replyto: formData.email,
          message: `NEW PROPERTY LEAD\n\nSeller: ${formData.sellerName}\nPhone: ${formData.phone}\nEmail: ${formData.email}\nAddress: ${formData.propertyAddress}\nState: ${formData.state}\nCounty: ${formData.county}\nTimeline: ${formData.timeline}\nReason: ${formData.reasoning}\nNotes: ${formData.additionalInfo}`,
          ...formData
        })
      });
      if (response.ok) setFormStep(4);
    } catch (error) {
      alert(`Submission failed. Please call us at ${PHONE_NUMBER}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const faqs = [
    { q: "How quickly can I get paid?", a: "Once we agree on a price, we typically close within 14-21 days using a reputable local title company or attorney." },
    { q: "Do I have to pay for a survey?", a: "No. If a survey is required for the sale, we cover 100% of that cost. You pay zero out-of-pocket." },
    { q: "What if I owe back taxes?", a: "Not a problem. We will pay off any delinquent taxes directly at closing, deducting them from the final purchase price." },
    { q: "Do I need to travel to the property or meet in person?", a: "No. The entire process can be handled remotely via email, phone, and overnight mail." }
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="relative pt-48 pb-32 overflow-hidden">
        <div className="absolute top-0 right-0 w-2/3 h-full bg-gradient-to-l from-emerald-50/70 to-transparent -z-10" />
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
              <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 px-4 py-2 rounded-full text-xs font-bold tracking-widest uppercase mb-6">
                <SafeIcon icon={FiActivity} className="animate-pulse" /> Direct Land Acquisitions
              </div>
              <h1 className="text-6xl md:text-8xl font-extrabold text-[#0a2e24] leading-[1] mb-8 tracking-tight">
                Land Selling <span className="text-emerald-600 block">Made Simple.</span>
              </h1>
              <p className="text-xl text-gray-600 mb-10 leading-relaxed max-w-lg">
                We buy vacant land nationwide for cash. No commissions, no surveys, no cleaning. Get a direct offer in 48 hours.
              </p>
              <button onClick={() => document.getElementById('offer-section').scrollIntoView({ behavior: 'smooth' })} className="btn-modern text-lg px-10 py-5">
                Get My Cash Offer <SafeIcon icon={FiArrowRight} className="ml-3 inline-block" />
              </button>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="relative hidden lg:block">
              <div className="aspect-[4/5] rounded-[4rem] overflow-hidden shadow-3xl transform rotate-1">
                <img src="https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=1200&q=80" className="w-full h-full object-cover" alt="Scenic American vacant land for sale" />
              </div>
              <div className="absolute -bottom-10 -left-10 glass-card p-8 rounded-3xl shadow-2xl max-w-xs">
                <div className="flex gap-4 items-center">
                  <div className="p-3 bg-emerald-100 rounded-2xl"><SafeIcon icon={FiZap} className="text-emerald-700 text-2xl" /></div>
                  <div>
                    <h4 className="font-bold text-emerald-900">48-Hour Offers</h4>
                    <p className="text-sm text-gray-500">Fast analysis & valuation</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Advantage Section */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-24">
            <h2 className="text-4xl md:text-6xl font-extrabold text-[#0a2e24] mb-6">The Quant Asset Advantage</h2>
            <p className="text-xl text-gray-500 max-w-2xl mx-auto">We aren't agents looking for a listing. We are professional land investors looking to buy.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: FiDollarSign, title: "Zero Commissions", desc: "Keep 100% of the offer price. No 6% realtor fees or hidden transaction costs." },
              { icon: FiFileText, title: "We Pay Closing Costs", desc: "Title insurance, escrow fees, and recording costs are all on us. Not you." },
              { icon: FiZap, title: "Fast Liquidity", desc: "Turn illiquid real estate into usable cash in weeks, not years on the market." }
            ].map((item, i) => (
              <article key={i} className="p-10 rounded-[3rem] bg-emerald-50/50 border border-emerald-100/50 hover:bg-emerald-50 transition-colors">
                <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg mb-8 text-emerald-700">
                  <SafeIcon icon={item.icon} className="text-3xl" />
                </div>
                <h3 className="text-2xl font-bold text-emerald-900 mb-4">{item.title}</h3>
                <p className="text-gray-500 leading-relaxed">{item.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-32 bg-emerald-900 text-white overflow-hidden relative">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-extrabold mb-6">The Smart Way to Sell</h2>
            <p className="text-emerald-200 text-xl">Compare our process to a traditional realtor listing.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-px bg-emerald-800 rounded-[3rem] overflow-hidden border border-emerald-700 shadow-2xl">
            <div className="p-12 bg-emerald-900/50">
              <h3 className="text-2xl font-bold mb-8 flex items-center gap-3"><SafeIcon icon={FiUserCheck} /> Selling to Quant Asset</h3>
              <ul className="space-y-6">
                <li className="flex items-start gap-4 text-emerald-100"><SafeIcon icon={FiCheck} className="mt-1 text-emerald-400" /> Cash offer in 48 hours</li>
                <li className="flex items-start gap-4 text-emerald-100"><SafeIcon icon={FiCheck} className="mt-1 text-emerald-400" /> We pay all closing costs & fees</li>
                <li className="flex items-start gap-4 text-emerald-100"><SafeIcon icon={FiCheck} className="mt-1 text-emerald-400" /> Close in as little as 14 days</li>
              </ul>
            </div>
            <div className="p-12 bg-white/5 backdrop-blur-sm">
              <h3 className="text-2xl font-bold mb-8 flex items-center gap-3 text-emerald-300">Traditional Listing</h3>
              <ul className="space-y-6 opacity-60">
                <li className="flex items-start gap-4">Months or years of waiting</li>
                <li className="flex items-start gap-4">6% Commission + 2% closing costs</li>
                <li className="flex items-start gap-4">High risk of buyer fall-out</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Funnel Section */}
      <section id="offer-section" className="py-32 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-extrabold text-[#0a2e24] mb-6">Request Your Cash Offer</h2>
            <p className="text-xl text-gray-500">Fast, confidential, and 100% free.</p>
          </div>
          <div className="bg-emerald-50 rounded-[3rem] p-8 md:p-12 shadow-2xl shadow-emerald-900/5 relative overflow-hidden border border-emerald-100">
            <div className="absolute top-0 left-0 w-full h-2 bg-emerald-100">
              <motion.div className="h-full bg-emerald-600" animate={{ width: `${(formStep / 4) * 100}%` }} />
            </div>
            <form onSubmit={submitForm}>
              <AnimatePresence mode="wait">
                {formStep === 1 && (
                  <motion.div key="s1" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-8">
                    <div className="flex justify-between items-end"><h3 className="text-3xl font-bold text-emerald-900">Property Info</h3><span className="text-emerald-600 font-black">01/03</span></div>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="md:col-span-2"><label className="label-modern">Address / Location</label><input type="text" name="propertyAddress" value={formData.propertyAddress} onChange={handleInputChange} className="input-modern bg-white" placeholder="Street or GPS Coordinates" /></div>
                      <div><label className="label-modern">State *</label><input type="text" name="state" value={formData.state} onChange={handleInputChange} required className="input-modern bg-white" placeholder="e.g. Texas" /></div>
                      <div><label className="label-modern">County *</label><input type="text" name="county" value={formData.county} onChange={handleInputChange} required className="input-modern bg-white" placeholder="e.g. Polk" /></div>
                    </div>
                    <div className="flex justify-end"><button type="button" onClick={() => setFormStep(2)} className="btn-modern">Next Step</button></div>
                  </motion.div>
                )}
                {formStep === 2 && (
                  <motion.div key="s2" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-8">
                    <div className="flex justify-between items-end"><h3 className="text-3xl font-bold text-emerald-900">Contact Details</h3><span className="text-emerald-600 font-black">02/03</span></div>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="md:col-span-2"><label className="label-modern">Full Name *</label><input type="text" name="sellerName" value={formData.sellerName} onChange={handleInputChange} required className="input-modern bg-white" /></div>
                      <div>
                        <label className="label-modern">Phone *</label>
                        <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} required className="input-modern bg-white mb-2" />
                        <p className="text-[10px] text-gray-400 leading-tight">By providing your phone number, you agree to receive SMS messages from us regarding your property. Reply STOP to opt-out. Msg/data rates apply.</p>
                      </div>
                      <div><label className="label-modern">Email *</label><input type="email" name="email" value={formData.email} onChange={handleInputChange} required className="input-modern bg-white" /></div>
                    </div>
                    <div className="flex justify-between items-center"><button type="button" onClick={() => setFormStep(1)} className="text-gray-400 font-bold">Back</button><button type="button" onClick={() => setFormStep(3)} className="btn-modern">Next Step</button></div>
                  </motion.div>
                )}
                {formStep === 3 && (
                  <motion.div key="s3" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-8">
                    <div className="flex justify-between items-end"><h3 className="text-3xl font-bold text-emerald-900">Final Step</h3><span className="text-emerald-600 font-black">03/03</span></div>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div><label className="label-modern">Timeline *</label><select name="timeline" value={formData.timeline} onChange={handleInputChange} required className="input-modern bg-white text-gray-500"><option value="">Choose Timeline</option><option value="ASAP">ASAP</option><option value="1 month">Within 1 Month</option></select></div>
                      <div><label className="label-modern">Reason *</label><select name="reasoning" value={formData.reasoning} onChange={handleInputChange} required className="input-modern bg-white text-gray-500"><option value="">Reason for Selling</option><option value="Inherited">Inherited</option><option value="Taxes">Back Taxes</option><option value="Cash">Need Cash</option></select></div>
                    </div>
                    <div className="flex justify-between items-center"><button type="button" onClick={() => setFormStep(2)} className="text-gray-400 font-bold">Back</button><button type="submit" disabled={isSubmitting} className="btn-modern px-12">{isSubmitting ? 'Submitting...' : 'Request Offer'}</button></div>
                  </motion.div>
                )}
                {formStep === 4 && (
                  <motion.div key="success" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-12">
                    <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6"><SafeIcon icon={FiCheck} className="text-4xl text-emerald-600" /></div>
                    <h3 className="text-3xl font-bold text-emerald-900 mb-2">Offer Request Received</h3>
                    <p className="text-gray-500 mb-8">We will be in touch within 48 hours.</p>
                    <button onClick={() => setFormStep(1)} className="text-emerald-700 font-bold hover:underline">Submit another parcel</button>
                  </motion.div>
                )}
              </AnimatePresence>
            </form>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-32 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-4xl font-extrabold text-emerald-900 mb-4">Common Questions</h2>
            <p className="text-gray-500">Everything you need to know about the process.</p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
                <button 
                  onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                  className="w-full flex justify-between items-center p-6 text-left hover:bg-emerald-50/50 transition-colors"
                >
                  <span className="font-bold text-emerald-900 text-lg">{faq.q}</span>
                  <SafeIcon icon={FiHelpCircle} className={`text-emerald-600 transition-transform ${activeFaq === i ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {activeFaq === i && (
                    <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden bg-emerald-50/30">
                      <p className="p-6 text-gray-600 leading-relaxed border-t border-emerald-50">{faq.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function App() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <HashRouter>
      <ScrollToTop />
      <div className="min-h-screen bg-[#fafafa]">
        {/* Navigation */}
        <header>
          <nav className={`fixed top-0 w-full z-[100] transition-all duration-500 ${scrolled ? 'py-4' : 'py-8'}`}>
            <div className="max-w-7xl mx-auto px-6">
              <div className="glass-card rounded-[2rem] px-8 py-4 flex justify-between items-center shadow-2xl shadow-black/5 border-white/40">
                <Link to="/" className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-emerald-700 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-700/20">
                    <SafeIcon icon={FiTarget} className="text-white text-xl" />
                  </div>
                  <span className="text-xl font-extrabold tracking-tight text-emerald-900 hidden sm:block">QUANT ASSET</span>
                </Link>
                <div className="flex items-center gap-6">
                  <a href={`tel:${PHONE_NUMBER.replace(/-/g, '')}`} className="hidden md:flex items-center gap-2 text-emerald-900 font-bold hover:text-emerald-800 transition-colors">
                    <SafeIcon icon={FiPhone} /> {PHONE_NUMBER}
                  </a>
                  <button onClick={() => document.getElementById('offer-section')?.scrollIntoView({ behavior: 'smooth' })} className="bg-emerald-900 text-white px-6 py-2.5 rounded-xl font-bold text-sm hover:bg-emerald-800 transition-all active:scale-95">
                    Get Offer
                  </button>
                </div>
              </div>
            </div>
          </nav>
        </header>

        <main>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsConditions />} />
          </Routes>
        </main>

        {/* Footer */}
        <footer className="bg-white py-20 border-t border-gray-100">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid md:grid-cols-12 gap-12 mb-20">
              <div className="md:col-span-6">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 bg-emerald-900 rounded-2xl flex items-center justify-center shadow-xl"><SafeIcon icon={FiTarget} className="text-white text-2xl" /></div>
                  <span className="text-2xl font-black text-emerald-900 uppercase">Quant Asset</span>
                </div>
                <p className="text-gray-500 text-lg leading-relaxed max-w-sm">Professional land acquisitions nationwide. We help landowners liquidate unwanted property quickly and fairly.</p>
              </div>
              <div className="md:col-span-3">
                <h4 className="font-black text-emerald-900 mb-6 uppercase tracking-widest text-sm">Contact</h4>
                <ul className="space-y-4 text-gray-500">
                  <li className="flex items-center gap-2 font-bold">
                    <SafeIcon icon={FiPhone} /> 
                    <a href={`tel:${PHONE_NUMBER.replace(/-/g, '')}`} className="hover:text-emerald-700 transition-colors">{PHONE_NUMBER}</a>
                  </li>
                  <li className="flex items-start gap-2 font-bold">
                    <SafeIcon icon={FiMail} className="mt-1 flex-shrink-0" /> 
                    <a href={`mailto:${EMAIL_ADDRESS}`} className="hover:text-emerald-700 transition-colors break-all md:break-words">{EMAIL_ADDRESS}</a>
                  </li>
                </ul>
              </div>
              <div className="md:col-span-3">
                <h4 className="font-black text-emerald-900 mb-6 uppercase tracking-widest text-sm">Trust</h4>
                <ul className="space-y-4 text-emerald-800 font-black text-sm">
                  <li className="flex items-center gap-2"><SafeIcon icon={FiShield} /> SECURE ENCRYPTION</li>
                  <li className="flex items-center gap-2"><SafeIcon icon={FiCheck} /> VERIFIED BUYER</li>
                </ul>
              </div>
            </div>
            <div className="pt-12 border-t border-gray-100 text-gray-400 text-sm font-medium flex flex-col md:flex-row justify-between gap-6">
              <span>&copy; 2024 Quant Asset Partners LLC. All rights reserved.</span>
              <div className="flex gap-8">
                <Link to="/privacy" className="hover:text-emerald-700 transition-colors">Privacy Policy</Link>
                <Link to="/terms" className="hover:text-emerald-700 transition-colors">Terms of Service</Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </HashRouter>
  );
}

export default App;